import { WALKABLE_TILES } from './DungeonGenerator.js';

/**
 * A* pathfinding on the dungeon tile grid.
 * 4-directional movement only (no diagonal) to avoid corner-cutting.
 */
export class Pathfinding {
  /**
   * @param {number[][]} tiles - 2D tile grid from DungeonGenerator
   * @param {Set<number>} walkableTiles - Set of walkable tile type constants
   */
  constructor(tiles, walkableTiles) {
    this.tiles = tiles;
    this.walkableTiles = walkableTiles || WALKABLE_TILES;
    this.height = tiles.length;
    this.width = tiles[0].length;
  }

  /**
   * Find a path from start to end in tile coordinates using A*.
   * @param {number} startTileX - Start column
   * @param {number} startTileY - Start row
   * @param {number} endTileX - End column
   * @param {number} endTileY - End row
   * @param {number} maxSteps - Maximum nodes to explore before giving up
   * @returns {Array<{x: number, y: number}>|null} Array of tile positions, or null if no path
   */
  findPath(startTileX, startTileY, endTileX, endTileY, maxSteps = 200) {
    // Bounds check
    if (!this._inBounds(startTileX, startTileY) || !this._inBounds(endTileX, endTileY)) {
      return null;
    }

    // Start or end not walkable
    if (!this._isWalkable(startTileX, startTileY) || !this._isWalkable(endTileX, endTileY)) {
      return null;
    }

    // Already at the goal
    if (startTileX === endTileX && startTileY === endTileY) {
      return [];
    }

    // A* open set as a simple array (sorted by f-score on extraction)
    const openSet = [];
    // Closed set tracked via a flat key
    const closedSet = new Set();
    // Cost and parent tracking via Maps keyed by "x,y"
    const gScore = new Map();
    const fScore = new Map();
    const cameFrom = new Map();

    const startKey = startTileX + ',' + startTileY;
    gScore.set(startKey, 0);
    fScore.set(startKey, this._heuristic(startTileX, startTileY, endTileX, endTileY));
    openSet.push({ x: startTileX, y: startTileY, f: fScore.get(startKey) });

    // 4-directional neighbors
    const dirs = [
      { dx: 0, dy: -1 }, // up
      { dx: 0, dy: 1 },  // down
      { dx: -1, dy: 0 }, // left
      { dx: 1, dy: 0 },  // right
    ];

    let steps = 0;

    while (openSet.length > 0 && steps < maxSteps) {
      steps++;

      // Find node with lowest f-score
      let bestIdx = 0;
      for (let i = 1; i < openSet.length; i++) {
        if (openSet[i].f < openSet[bestIdx].f) {
          bestIdx = i;
        }
      }
      const current = openSet[bestIdx];
      openSet.splice(bestIdx, 1);

      const currentKey = current.x + ',' + current.y;

      // Reached the goal
      if (current.x === endTileX && current.y === endTileY) {
        return this._reconstructPath(cameFrom, currentKey);
      }

      closedSet.add(currentKey);

      for (const dir of dirs) {
        const nx = current.x + dir.dx;
        const ny = current.y + dir.dy;
        const neighborKey = nx + ',' + ny;

        if (!this._inBounds(nx, ny)) continue;
        if (!this._isWalkable(nx, ny)) continue;
        if (closedSet.has(neighborKey)) continue;

        const tentativeG = gScore.get(currentKey) + 1;

        if (!gScore.has(neighborKey) || tentativeG < gScore.get(neighborKey)) {
          cameFrom.set(neighborKey, currentKey);
          gScore.set(neighborKey, tentativeG);
          const f = tentativeG + this._heuristic(nx, ny, endTileX, endTileY);
          fScore.set(neighborKey, f);

          // Add to open set if not already there
          const alreadyInOpen = openSet.some(n => n.x === nx && n.y === ny);
          if (!alreadyInOpen) {
            openSet.push({ x: nx, y: ny, f });
          }
        }
      }
    }

    // No path found (or maxSteps exceeded)
    return null;
  }

  /**
   * Manhattan distance heuristic.
   */
  _heuristic(x1, y1, x2, y2) {
    return Math.abs(x1 - x2) + Math.abs(y1 - y2);
  }

  _inBounds(x, y) {
    return x >= 0 && x < this.width && y >= 0 && y < this.height;
  }

  _isWalkable(x, y) {
    return this.walkableTiles.has(this.tiles[y][x]);
  }

  /**
   * Reconstruct path from cameFrom map.
   * Returns array of {x, y} tile positions (excludes start, includes end).
   */
  _reconstructPath(cameFrom, endKey) {
    const path = [];
    let current = endKey;

    while (cameFrom.has(current)) {
      const [x, y] = current.split(',').map(Number);
      path.push({ x, y });
      current = cameFrom.get(current);
    }

    path.reverse();
    return path;
  }
}

/**
 * Line-of-sight check using Bresenham-style ray march through the tile grid.
 * Returns true if there is a clear line between two pixel positions.
 * @param {number} x1 - Start pixel X
 * @param {number} y1 - Start pixel Y
 * @param {number} x2 - End pixel X
 * @param {number} y2 - End pixel Y
 * @param {number[][]} tiles - Dungeon tile grid
 * @param {number} tileSize - Tile size in pixels
 * @returns {boolean} True if line of sight is clear
 */
export function hasLineOfSight(x1, y1, x2, y2, tiles, tileSize) {
  const height = tiles.length;
  const width = tiles[0].length;

  // Convert to tile coords
  const startCol = Math.floor(x1 / tileSize);
  const startRow = Math.floor(y1 / tileSize);
  const endCol = Math.floor(x2 / tileSize);
  const endRow = Math.floor(y2 / tileSize);

  // Bresenham line algorithm in tile space
  let col = startCol;
  let row = startRow;
  const dcol = Math.abs(endCol - col);
  const drow = Math.abs(endRow - row);
  const scol = col < endCol ? 1 : -1;
  const srow = row < endRow ? 1 : -1;
  let err = dcol - drow;

  while (true) {
    // Check current tile
    if (col < 0 || col >= width || row < 0 || row >= height) {
      return false;
    }
    if (!WALKABLE_TILES.has(tiles[row][col])) {
      return false;
    }

    // Reached end tile
    if (col === endCol && row === endRow) {
      return true;
    }

    const e2 = 2 * err;
    if (e2 > -drow) {
      err -= drow;
      col += scol;
    }
    if (e2 < dcol) {
      err += dcol;
      row += srow;
    }
  }
}
